/**
 * @flow
 */

import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Modal, Header, Button, Icon, Message } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { GET_MANUFACTURERS_QUERY } from './ManufacturersList';
import type { ModalDialog, Manufacturer } from '../../../../type';
import { get } from 'lodash';

type DeleteManufacturerDialogProps = {
    manufacturer: Manufacturer,
} & ModalDialog;

const intlMessages = defineMessages({
    existingsError: {
        id: 'pages.settings.manufacturers.form.existingsError',
        defaultMessage: 'This manufacturer does not exist',
    },
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

const DELETE_MANUFACTURER_QUERY = gql`
    mutation deleteManufacturer($input: DeleteManufacturerInput!) {
        deleteManufacturer(input: $input) {
            manufacturerId
        }
    }
`;

const MessageBlock = styled(Message)`
    background-color: transparent !important;
    border: 2px solid #ff4545;
    color: #ff4545 !important;
    font-weight: bold;
    box-shadow: none !important;
`;

const DeleteManufacturerDialog = ({
    manufacturer,
    trigger,
    open,
    onClose,
    intl,
}: DeleteManufacturerDialogProps) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const [error, setError] = useState(null);

    const [deleteManufacturer] = useMutation(DELETE_MANUFACTURER_QUERY, {
        // Remove the deleted manufacturer from the query cache
        update: (store, { data: { deleteManufacturer: result } }) => {
            const data = store.readQuery({
                query: GET_MANUFACTURERS_QUERY,
                variables: { salon },
            });
            store.writeQuery({
                query: GET_MANUFACTURERS_QUERY,
                variables: { salon },
                data: {
                    ...data,
                    viewer: {
                        ...data.viewer,
                        salon: {
                            ...data.viewer.salon,
                            manufacturers: {
                                ...data.viewer.salon.manufacturers,
                                nodes: data.viewer.salon.manufacturers.nodes.filter(
                                    m => m.id !== result.manufacturerId,
                                ),
                            },
                        },
                    },
                },
            });
        },
    });

    const onDelete = async () => {
        try {
            await deleteManufacturer({
                variables: { input: { manufacturerId: manufacturer.id } },
                // Optimistic response compensates network latency and removes
                // the manufacturer before the response is received
                optimisticResponse: {
                    __typename: 'Mutation',
                    deleteManufacturer: {
                        __typename: 'DeleteManufacturerPayload',
                        manufacturerId: manufacturer.id,
                    },
                },
            });
            onClose();
        } catch (err) {
            const graphQLError = err.graphQLErrors && err.graphQLErrors[0];
            if (
                graphQLError &&
                graphQLError.name === 'NotExists' &&
                get(graphQLError, 'data.error') === 'ID_DOES_NOT_EXISTS'
            ) {
                setError(intl.formatMessage(intlMessages.existingsError));
                return;
            }
            setError(intl.formatMessage(intlMessages.defaultErrorMessage));
        }
    };

    return (
    <Modal trigger={trigger} open={open} onClose={onClose} size="small" basic>
        <Header icon>
            <Icon name="remove" />
            <FormattedMessage
                id="pages.settings.manufacturers.form.delete.deleteHeader"
                defaultMessage="Delete Manufacturer"
            />
        </Header>
        <Modal.Content>
            <FormattedMessage
                id="pages.settings.manufacturers.form.delete.deleteText"
                defaultMessage="Are you sure you want to delete {manufacturerName}?"
                values={{
                    manufacturerName: manufacturer.name,
                }}
            />
            {error && (
                <MessageBlock error>
                    <Icon name="warning sign" />
                    {error}
                </MessageBlock>
            )}
        </Modal.Content>
        <Modal.Actions>
            <Button onClick={onClose} color="red" basic inverted>
                <Icon name="remove" />
                <FormattedMessage
                    id="pages.settings.manufacturers.form.delete.no"
                    defaultMessage="No"
                />
            </Button>
            <Button onClick={onDelete} color="green" inverted>
                <Icon name="checkmark" />
                <FormattedMessage
                    id="pages.settings.manufacturers.form.delete.yes"
                    defaultMessage="Yes"
                />
            </Button>
        </Modal.Actions>
    </Modal>
    );
};

export default injectIntl(DeleteManufacturerDialog);
